import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client/react';
import { Tooltip } from '@progress/kendo-react-tooltip';
import ConfirmationDialog from '@components-clean/core/confirmation-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { CtqPageRiskFields } from '@pages-clean/risk-assessment/critical-to-quality-factors/types';
import { CTQFactorsContext } from '@pages-clean/risk-assessment/interfaces/context';
import { ASSIGN_RISKS, CTQ_WIDGET_VALUES, GET_CRITICAL_VARIABLES } from '@queries/risk-assessment';

interface UnassignRiskProps {
    onClose: () => void;
    onSubmit: (result: string) => void;
    ctqId: string;
    risk: CtqPageRiskFields;
    risks: CtqPageRiskFields[];
}

const UnassignRiskDialog = ({ onClose, onSubmit, ctqId, risk, risks }: UnassignRiskProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'ctqf.ctqFactors.dialogs.unassignRisk' });
    const { current } = useContext(CTQFactorsContext);

    const [assignRisks] = useMutation(ASSIGN_RISKS, {
        refetchQueries: [
            {
                query: GET_CRITICAL_VARIABLES,
                variables: { studyId: current.study?.id },
            },
            {
                query: CTQ_WIDGET_VALUES,
                variables: { studyId: current.study?.id },
            },
        ],
    });

    const handleSubmit = async () => {
        const filteredRiskIds = risks.filter((r) => r.id !== risk.id).map((r) => r.id);

        await assignRisks({
            variables: {
                id: ctqId,
                riskIds: filteredRiskIds,
            },
        })
            .then(() => {
                onSubmit('success');
            })
            .catch(() => {
                onSubmit('error');
            });
    };

    return (
        <ConfirmationDialog
            title={t('title')}
            dataTestId="confirmation-dialog"
            onClose={() => onClose()}
            onSubmit={() => handleSubmit()}
        >
            <div>
                <InfoField
                    type="Info"
                    message={
                        <div style={{ width: '438px', margin: '0 0' }}>
                            <div className="k-d-flex k-align-items-center">
                                <div className="k-font-medium">{t('CtQDescription')}</div>
                            </div>
                            <Tooltip anchorElement="target" position="auto" parentTitle style={{ maxWidth: '600px' }}>
                                <div title={current?.selectionState.selectedItem?.description ?? ''}>
                                    {(() => {
                                        const desc = current?.selectionState.selectedItem?.description ?? '';
                                        return desc.length > 600 ? `${desc.slice(0, 600)}...` : desc;
                                    })()}
                                </div>
                            </Tooltip>
                            <br />
                            <div className="k-font-medium">{t('riskStatement')}</div>
                            <div>{risk?.statement?.slice(0, 600)}</div>
                        </div>
                    }
                />
                <div style={{ textAlign: 'center', marginTop: '30px', marginBottom: '18px' }}>{t('message')}</div>
            </div>
        </ConfirmationDialog>
    );
};

export default UnassignRiskDialog;
